import { Injectable } from '@angular/core';
import { BillingService } from '@core/services/billing.service';
import { ErrorSanitizerService } from '@core/services/error-sanitizer.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import {
  catchError,
  delayWhen,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { BillingSelectors } from '../../selectors';
import { filterOutNull } from '@shared/rxjs/filter-out-null.operator';
import { ProductsService } from '@core/services/products.service';
import { QuoteService } from '@core/services/quote.service';
import { BillingActions } from '@core/store/actions';
import { PayBindService } from '@core/services/pay-bind.service';
@Injectable()
export class BillingEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private payBindService: PayBindService,
    private billingService: BillingService,
    private errorSanitizerService: ErrorSanitizerService,
    private productsService: ProductsService,
    private quoteService: QuoteService
  ) {}

  loadDownPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadDownPayment),
      delayWhen(() =>
        this.quoteService.getQuoteInProgressCount().pipe(
          filter((inProgressCount) => !inProgressCount),
          take(1)
        )
      ),
      switchMap(() =>
        this.payBindService.getProductsByBillingAccount().pipe(take(1))
      ),
      map((productsByBillingAccount) =>
        productsByBillingAccount.filter(
          (pbb) => pbb.billingAccountType === 'billing'
        )
      ),
      tap((productsByBillingAccount) =>
        this.billingService.adjustDownPaymentCallCount(
          productsByBillingAccount.length
        )
      ),
      mergeMap(
        (productsByBillingAccount) =>
          productsByBillingAccount.length
            ? from(productsByBillingAccount)
            : of({ productIds: [] }) // dummy for escrow-only cases
      ),
      mergeMap(({ productIds }) =>
        this.store
          .select(
            BillingSelectors.buildDownPaymentExperienceRequest(productIds)
          )
          .pipe(take(1))
      ),
      mergeMap((request) => {
        if (!request) {
          return of(BillingActions.downPaymentNotNeeded());
        } else {
          return this.billingService.downPaymentExperience(request).pipe(
            switchMap((response) =>
              from([
                BillingActions.loadDownPaymentSuccess({ payload: response }),
                BillingActions.adjustDownPaymentCallCount({ d: -1 }),
              ])
            ),
            catchError((error) => {
              return from([
                BillingActions.loadDownPaymentFail({
                  error: this.errorSanitizerService.sanitizeError(error),
                }),
                BillingActions.adjustDownPaymentCallCount({ d: -1 }),
              ]);
            })
          );
        }
      })
    )
  );

  loadDownPaymentIfAllProductsRated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadDownPaymentIfAllProductsRated),
      withLatestFrom(this.productsService.getSelectedProducts()),
      map(([action, products]) => {
        if (
          products.every(
            (p) => p.quoteStatus === 'Quoted' || p.quoteStatus === 'Binding'
          )
        ) {
          return BillingActions.loadDownPayment();
        } else {
          return null;
        }
      }),
      filterOutNull()
    )
  );
}
